import React from 'react';
import { styles } from './MiniProductTile.css';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { ProgressiveImage } from '../hooks/useProgressiveImage/useProgressiveImage';
import dynamicallyResizeShopifyImageUrl from '@jbhi-fi/utils/src/resizeShopifyImage/resizeShopifyImage';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { getTitleCase } from '@jbhi-fi/utils/src/getTitleCase/getTitleCase';
import { spacing } from '@jbhi-fi/lanyard-ui/themes';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';

export type MiniProductTileProps = {
  hit: AlgoliaProductHit;
  shouldUseProgressiveImage?: boolean;
  heading: string;
  subheading?: string;
  footer?: string;
  number?: number;
  href?: string;
  onClick: (e: React.MouseEvent, hit: AlgoliaProductHit) => void;
};

export const MiniProductTile: React.FC<MiniProductTileProps> = ({
  hit,
  href = `/products/${hit.handle}`,
  shouldUseProgressiveImage = false,
  heading,
  subheading,
  footer,
  number,
  onClick,
}) => {
  const showNumber = typeof number === 'number';
  const imgSrc = hit.product_image;

  const imgProps = {
    src: dynamicallyResizeShopifyImageUrl(imgSrc, '380x380'),
    alt: hit.display.images?.[0]?.ImageName ?? heading,
    className: styles.cover,
    width: 145,
    height: 145,
    srcSet: [
      `${dynamicallyResizeShopifyImageUrl(imgSrc, '189x189')} 189w`,
      `${dynamicallyResizeShopifyImageUrl(imgSrc, '380x380')} 380w`,
    ].join(','),
  };

  return (
    <a
      href={href}
      className={styles.container}
      onClick={e => onClick(e, hit)}
      style={{ paddingTop: showNumber ? spacing(7.5) : 0 }}
    >
      {showNumber ? <DomFont className={styles.number}>{number}</DomFont> : null}
      {shouldUseProgressiveImage ? (
        <ProgressiveImage placeholderSrc={dynamicallyResizeShopifyImageUrl(imgSrc, '32x32')} {...imgProps} />
      ) : (
        <img {...imgProps} alt={imgProps.alt} />
      )}
      {heading ? (
        <span className={classNames(styles.text, styles.heading({ hasSubHeading: Boolean(subheading) }))}>
          {heading}
        </span>
      ) : null}
      {subheading ? <span className={styles.text}>{getTitleCase(subheading)}</span> : null}
      {footer ? <span className={classNames(styles.text, styles.footer)}>{footer}</span> : null}
    </a>
  );
};
